export const WIDGET_LAYOUTS = [
  { id: 'modal', label: 'Modal', description: 'Centered window over a dimmed page' },
  { id: 'popup', label: 'Popup', description: 'Small panel just above the button' },
  { id: 'side-modal', label: 'Side panel', description: 'Floating card along the right edge' },
  { id: 'sidebar', label: 'Sidebar', description: 'Full-height panel on the right' },
];

export const DEFAULT_WIDGET_MESSAGE = "Hello! I'm the manteo.ai assistant. I can help analyze your datasets. How can I assist you today?";

export const DEFAULT_WIDGET_QUESTIONS = [
  'What questions can you answer?',
  'Brief description of the available datasets',
  'How can I visualize my data on a map?',
];

const DEFAULT_COLOR = '#059669';

export const normalizeLayout = (layout) => (
  WIDGET_LAYOUTS.some(item => item.id === layout) ? layout : 'modal'
);

const attr = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;');

const jsString = (value) => String(value)
  .replace(/\\/g, '\\\\')
  .replace(/'/g, "\\'")
  .replace(/\r/g, '')
  .replace(/\n/g, '\\n');

const questionAttributes = (questions) => questions.slice(0, 6).map((question, index) => ({
  name: index === 0 ? 'data-question' : `data-question-${index + 1}`,
  value: question,
}));

export const widgetScriptAttributes = ({
  publicKey,
  layout = 'modal',
  color = DEFAULT_COLOR,
  message = DEFAULT_WIDGET_MESSAGE,
  questions = DEFAULT_WIDGET_QUESTIONS,
} = {}) => {
  const attributes = {
    'data-key': publicKey,
    'data-color': color,
    'data-message': message,
  };
  questionAttributes(questions).forEach(({ name, value }) => {
    attributes[name] = value;
  });
  attributes['data-layout'] = normalizeLayout(layout);
  return attributes;
};

export const buildEmbedScript = ({
  origin,
  publicKey,
  layout = 'modal',
  color = DEFAULT_COLOR,
  message = DEFAULT_WIDGET_MESSAGE,
  questions = DEFAULT_WIDGET_QUESTIONS,
} = {}) => {
  const attributes = widgetScriptAttributes({ publicKey, layout, color, message, questions });
  const lines = [
    '<script',
    `  src="${attr(origin)}/embed.js"`,
  ];
  Object.entries(attributes).forEach(([name, value]) => {
    lines.push(`  ${name}="${attr(value)}"`);
  });
  lines.push('  async');
  lines.push('></script>');
  return lines.join('\n');
};

const scriptSetup = ({ src, publicKey, layout, color, message, questions }) => {
  const lines = [
    '    const script = document.createElement(\'script\');',
    `    script.src = '${jsString(src)}';`,
    '    script.async = true;',
    `    script.setAttribute('data-key', '${jsString(publicKey)}');`,
    `    script.setAttribute('data-color', '${jsString(color)}');`,
    `    script.setAttribute('data-message', '${jsString(message)}');`,
  ];
  questionAttributes(questions).forEach(({ name, value }) => {
    lines.push(`    script.setAttribute('${name}', '${jsString(value)}');`);
  });
  lines.push(`    script.setAttribute('data-layout', '${jsString(normalizeLayout(layout))}');`);
  lines.push('    document.body.appendChild(script);');
  return lines.join('\n');
};

export const installGuides = ({
  origin,
  publicKey,
  layout = 'modal',
  color = DEFAULT_COLOR,
  message = DEFAULT_WIDGET_MESSAGE,
  questions = DEFAULT_WIDGET_QUESTIONS,
} = {}) => {
  const src = `${origin}/embed.js`;
  const html = buildEmbedScript({ origin, publicKey, layout, color, message, questions });
  const setup = scriptSetup({ src, publicKey, layout, color, message, questions });
  const questionAttrs = questionAttributes(questions)
    .map(({ name, value }) => `      ${name}="${attr(value)}"`)
    .join('\n');

  return [
    {
      id: 'html',
      label: 'HTML',
      guide: 'Paste this just before </body> on each page where the chat should appear.',
      code: html,
    },
    {
      id: 'react',
      label: 'React',
      guide: 'Render this once from your root component so the button is on every page.',
      code: [
        "import { useEffect } from 'react';",
        '',
        'export function ManteoWidget() {',
        '  useEffect(() => {',
        setup,
        '  }, []);',
        '',
        '  return null;',
        '}',
      ].join('\n'),
    },
    {
      id: 'next',
      label: 'Next.js',
      guide: 'Add this to the root layout. It loads after the page is interactive.',
      code: [
        "import Script from 'next/script';",
        '',
        'export function ManteoWidget() {',
        '  return (',
        '    <Script',
        `      src="${attr(src)}"`,
        '      strategy="afterInteractive"',
        `      data-key="${attr(publicKey)}"`,
        `      data-color="${attr(color)}"`,
        `      data-message="${attr(message)}"`,
        questionAttrs,
        `      data-layout="${attr(normalizeLayout(layout))}"`,
        '    />',
        '  );',
        '}',
      ].join('\n'),
    },
    {
      id: 'vue',
      label: 'Vue',
      guide: 'Load the script when the root component is mounted.',
      code: [
        "import { onMounted } from 'vue';",
        '',
        'onMounted(() => {',
        setup,
        '});',
      ].join('\n'),
    },
    {
      id: 'wordpress',
      label: 'WordPress',
      guide: 'Add a Custom HTML block in the footer, or paste this in your theme before </body>.',
      code: html,
    },
  ];
};
